import { type VariantProps, cva } from "class-variance-authority"
import { useEffect } from "react"
import { Fragment } from "react/jsx-runtime"
import { Temporal } from "temporal-polyfill"
import { ErrorMessage } from "@hookform/error-message"
import {
  CalendarDays,
  CalendarIcon,
  Clock,
  Hash,
  MinusCircleIcon,
  PlusCircle,
  RefreshCw,
  Settings2,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import {
  Controller,
  DefaultValues,
  FieldValues,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  UseFormProps,
  UseFormReturn,
} from "react-hook-form"

import { Calendar } from "@/components/ui/calendar"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { SelectInput } from "@/components/ui/select"
import { Combobox, MultiCombobox, SelectOption } from "@/components/ui/combobox"
import { ErrorHint } from "@/components/ui/errorhint"
import { Input, InputWithIcon } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { TestId } from "@/components/ui/layout"
import { VALIDATION_ERROR_KEYS } from "@/i18n"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  CustomClassFormValues,
  TeachingCalendar,
} from "@/types/teachers/customClasses"
import { useGetSpecialitiesQuery } from "@/api"
import { skipToken } from "@reduxjs/toolkit/query"
import { set, get } from "lodash-es"
import { RHFDirtyDraftRegistration } from "@/components/shared/dirty-draft-navigation"
import { useNavigate, useProps } from "@/lib/page-rendering"

type ClassFormFieldValues = FieldValues & {
  custom_class: CustomClassFormValues
}

export function useClassForm(
  classFormProps: UseFormProps<ClassFormFieldValues>,
) {
  const defaultValues: DefaultValues<ClassFormFieldValues> = {
    ...(classFormProps?.defaultValues ?? {}),
  }

  if (!get(defaultValues, "custom_class.timezone_id")) {
    set(
      defaultValues,
      "custom_class.timezone_id",
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    )
  }

  return useForm<ClassFormFieldValues>(classFormProps)
}

export default function Form({
  labelledBy,
  onSubmit,
  children,
  formId,
  methods,
}: {
  labelledBy: string
  onSubmit: (data: { custom_class: CustomClassFormValues }) => void
  children?: React.ReactNode
  formId: string
  methods: UseFormReturn<{ custom_class: CustomClassFormValues }>
}) {
  return (
    <FormProvider {...methods}>
      <RHFDirtyDraftRegistration />
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        aria-labelledby={labelledBy}
        className="flex flex-col gap-8"
        id={formId}
        data-testid="custom-class-form"
      >
        {children}
        <StructureSection />
      </form>
    </FormProvider>
  )
}

export function DetailsSection({
  selectedListingProfileId,
  children,
}: {
  selectedListingProfileId: number | null
  children?: React.ReactNode
}) {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<{ custom_class: CustomClassFormValues }>()
  const { t } = useTranslation(["custom_classes", "common"])
  const navigate = useNavigate()
  const { state_id } = useProps<{ state_id: number }>()
  const { states } = useProps<{ states: Array<SelectOption<number>> }>()
  const { subjects } = useProps<{ subjects: Array<SelectOption<number>> }>()

  const {
    data: specialities,
    isLoading: specialitiesLoading,
    isError: specialitiesError,
    refetch: refetchSpecialities,
  } = useGetSpecialitiesQuery(selectedListingProfileId ?? skipToken)

  const timezones = Intl.supportedValuesOf("timeZone")

  return (
    <Card>
      <CardHeader>
        <CardTitle className="mb-4 flex flex-row items-center gap-2">
          <Settings2 />
          <span id="details-section-title">
            {t("custom_classes:form.details.section_title")}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <fieldset
          aria-labelledby="details-section-title"
          className="flex max-w-4xl flex-col gap-6"
        >
          <div className="flex w-full max-w-4xl flex-col">
            <Label
              htmlFor="name"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t("custom_classes:form.details.name")}
            </Label>
            <Input
              id="name"
              autoComplete="off"
              {...register("custom_class.name", {
                required: t(VALIDATION_ERROR_KEYS.REQUIRED),
              })}
            />
            <ErrorMessage name="name" errors={errors} render={ErrorHint} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex grow flex-col">
              <Label
                htmlFor="state"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                {t("custom_classes:form.details.state")}
              </Label>
              <Combobox
                id="state"
                value={state_id}
                onChange={(state) => {
                  if (state !== null) {
                    navigate({
                      href: window.location.href,
                      data: {
                        state_id: state,
                      },
                      replace: true,
                      preserveScroll: true,
                      preserveUrl: true,
                    })
                  }
                }}
                options={states}
              />
              <ErrorMessage
                name="state_id"
                errors={errors}
                render={ErrorHint}
              />
            </div>
            <div className="flex grow flex-col">
              <Label
                htmlFor="subject"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                {t("custom_classes:form.details.subject")}
              </Label>
              <Controller
                name="custom_class.subject_id"
                control={control}
                rules={{ required: t(VALIDATION_ERROR_KEYS.REQUIRED) }}
                render={({ field }) => (
                  <Combobox
                    id="subject"
                    value={field.value}
                    onChange={field.onChange}
                    options={subjects || []}
                  />
                )}
              />
              <ErrorMessage
                name="subject_id"
                errors={errors}
                render={ErrorHint}
              />
            </div>
          </div>
          <div className="flex grow flex-col">
            <Label
              htmlFor="timezone"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t("custom_classes:form.details.timezone")}
            </Label>
            <Controller
              name="custom_class.timezone_id"
              rules={{ required: t(VALIDATION_ERROR_KEYS.REQUIRED) }}
              control={control}
              render={({ field }) => (
                <Combobox
                  id="timezone"
                  value={field.value}
                  onChange={field.onChange}
                  options={timezones.map((timezone: string) => ({
                    label: timezone,
                    value: timezone,
                  }))}
                />
              )}
            />
            <ErrorMessage
              name="timezone_id"
              errors={errors}
              render={ErrorHint}
            />
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex grow flex-col">
              <Label
                htmlFor="min_students"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                {t("custom_classes:form.details.min_students")}
              </Label>
              <Input
                id="min_students"
                type="number"
                min={1}
                {...register("custom_class.min_students", {
                  required: t(VALIDATION_ERROR_KEYS.REQUIRED),
                  valueAsNumber: true,
                  min: {
                    value: 1,
                    message: t(
                      VALIDATION_ERROR_KEYS.MUST_BE_GREATER_THAN_OR_EQUAL_TO,
                      {
                        value: "1",
                      },
                    ),
                  },
                })}
              />
              <ErrorMessage
                name="min_students"
                errors={errors}
                render={ErrorHint}
              />
            </div>
            <div className="flex grow flex-col">
              <Label
                htmlFor="max_students"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                {t("custom_classes:form.details.max_students")}
              </Label>
              <Input
                id="max_students"
                type="number"
                min={1}
                {...register("custom_class.max_students", {
                  required: t(VALIDATION_ERROR_KEYS.REQUIRED),
                  valueAsNumber: true,
                  min: {
                    value: watch("custom_class.min_students"),
                    message: t(
                      VALIDATION_ERROR_KEYS.MUST_BE_GREATER_THAN_OR_EQUAL_TO,
                      {
                        value: t("custom_classes:form.details.min_students"),
                      },
                    ),
                  },
                })}
              />
              <ErrorMessage
                name="max_students"
                errors={errors}
                render={ErrorHint}
              />
            </div>
          </div>
          <div className="flex grow flex-col">
            <Label
              htmlFor="num_classes"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t("custom_classes:form.details.num_classes")}
            </Label>
            <Input
              id="num_classes"
              type="number"
              min={1}
              {...register("custom_class.num_classes", {
                required: t(VALIDATION_ERROR_KEYS.REQUIRED),
                valueAsNumber: true,
              })}
            />
            <ErrorMessage
              name="num_classes"
              errors={errors}
              render={ErrorHint}
            />
          </div>
          {children}
          <div className="flex grow flex-col">
            <TestId testid="speciality-selection">
              <div className="flex max-w-4xl flex-col">
                <div className="flex flex-row items-end gap-2">
                  <div className="flex w-full flex-col">
                    <Label
                      htmlFor="speciality_id"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      {t("custom_classes:form.details.specialities")}
                    </Label>
                    {!selectedListingProfileId && (
                      <p
                        data-testid="specialities-unset"
                        className="text-muted-foreground py-2 text-sm"
                      >
                        {t(
                          "custom_classes:form.details.specialities_select_profile",
                        )}
                      </p>
                    )}
                    {selectedListingProfileId && specialitiesLoading && (
                      <div
                        data-testid="specialities-loading"
                        className="bg-muted h-10 w-full animate-pulse rounded-md"
                        aria-label={t("common:loading")}
                      />
                    )}
                    {selectedListingProfileId && specialitiesError && (
                      <div
                        data-testid="specialities-error"
                        className="flex items-center gap-2"
                      >
                        <p className="text-destructive text-sm">
                          {t(
                            "custom_classes:form.details.specialities_load_error",
                          )}
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => refetchSpecialities()}
                          aria-label={t("common:actions.retry")}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    {selectedListingProfileId && specialities && (
                      <Controller
                        name="custom_class.speciality_ids"
                        control={control}
                        render={({ field }) => (
                          <MultiCombobox
                            id="speciality_id"
                            selectPlaceholder={t(
                              "custom_classes:form.details.specialities_multiselect_placeholder",
                            )}
                            options={specialities}
                            selected={specialities.filter((speciality) =>
                              field.value.includes(speciality.value),
                            )}
                            onChange={(selected) =>
                              field.onChange(selected.map((item) => item.value))
                            }
                          />
                        )}
                      />
                    )}
                    <ErrorMessage
                      name="speciality_ids"
                      errors={errors}
                      render={ErrorHint}
                    />
                  </div>
                </div>
              </div>
            </TestId>
          </div>
        </fieldset>
      </CardContent>
    </Card>
  )
}

function StructureSection() {
  const { t } = useTranslation(["custom_classes"])
  const { control } = useFormContext<{ custom_class: CustomClassFormValues }>()
  return (
    <Card>
      <CardHeader>
        <CardTitle className="mb-4 flex flex-row items-center gap-2">
          <Settings2 />
          <span id="structure-section-title">
            {t("custom_classes:form.structure.section_title")}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <fieldset
          aria-labelledby="structure-section-title"
          className="grid grid-cols-2 gap-4"
        >
          <div className="flex flex-col gap-4">
            <div>
              <div className="flex grow flex-col">
                <Label
                  htmlFor="cycle_length"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  {t("custom_classes:form.structure.cycle_length")}
                </Label>
                <Controller
                  name="custom_class.cycle_length"
                  control={control}
                  render={({ field }) => (
                    <SelectInput
                      id="cycle_length"
                      value={field.value?.toString()}
                      onChange={(value) => field.onChange(parseInt(value, 10))}
                      options={[
                        {
                          value: "7",
                          label: t("custom_classes:form.structure.week", {
                            count: 1,
                          }),
                        },
                        {
                          value: "14",
                          label: t("custom_classes:form.structure.week", {
                            count: 2,
                          }),
                        },
                      ]}
                    />
                  )}
                />
              </div>
            </div>
            <div>
              <Label id="live_sessions" asChild>
                <div className="mb-1">
                  {t("custom_classes:form.structure.live_sessions")}
                </div>
              </Label>
              <SessionsInput
                labelledBy={"live_sessions"}
                name="live_sessions_attributes"
              />
            </div>

            <div>
              <Label id="student_led_sessions" asChild>
                <div className="mb-1">
                  {t("custom_classes:form.structure.student_led_sessions")}
                </div>
              </Label>
              <SessionsInput
                labelledBy="student_led_sessions"
                name="student_led_sessions_attributes"
              />
            </div>

            <div>
              <Label id="other_sessions" asChild>
                <div className="mb-1">
                  {t("custom_classes:form.structure.other_sessions")}
                </div>
              </Label>
              <SessionsInput
                labelledBy="other_sessions"
                name="other_sessions_attributes"
              />
            </div>
          </div>
          <TeachingYearPlanner />
        </fieldset>
      </CardContent>
    </Card>
  )
}

type SessionAttributeKeys = keyof Pick<
  CustomClassFormValues,
  | "live_sessions_attributes"
  | "student_led_sessions_attributes"
  | "other_sessions_attributes"
>

function SessionsInput({
  name,
  labelledBy,
}: {
  name: SessionAttributeKeys
  labelledBy: string
}) {
  const { t } = useTranslation(["common", "custom_classes"])
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<{ custom_class: CustomClassFormValues }>()

  const { append, remove, fields } = useFieldArray({
    control,
    name: `custom_class.${name}`,
  })

  return (
    <fieldset aria-labelledby={labelledBy} className="flex flex-col gap-3">
      {fields.map((field, index) => (
        <fieldset key={field.id} className="bg-muted/30 rounded-md p-3">
          <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-[1fr_1fr_auto]">
            <div className="flex flex-col gap-1">
              <label
                htmlFor={`${name}.${index}.count`}
                className="flex h-10 items-center text-sm font-medium"
              >
                {t("custom_classes:form.structure.sessions_input_count_label")}
              </label>
              <InputWithIcon
                id={`${name}.${index}.count`}
                Icon={Hash}
                type="number"
                min={1}
                max={20}
                placeholder={t(
                  "custom_classes:form.structure.sessions_input_count_placeholder",
                )}
                {...register(`custom_class.${name}.${index}.count` as const, {
                  required: VALIDATION_ERROR_KEYS.REQUIRED,
                  valueAsNumber: true,
                })}
              />
              <ErrorMessage
                errors={errors}
                name={`${name}.${index}.count` as const}
                render={ErrorHint}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor={`${name}.${index}.duration`}
                className="flex h-10 items-center text-sm font-medium"
              >
                {t(
                  "custom_classes:form.structure.sessions_input_duration_label",
                )}
              </label>
              <InputWithIcon
                id={`${name}.${index}.duration`}
                Icon={Clock}
                inputMode="numeric"
                pattern="^[1-9]\d*$"
                placeholder={t(
                  "custom_classes:form.structure.sessions_input_duration_placeholder",
                )}
                {...register(
                  `custom_class.${name}.${index}.duration` as const,
                  {
                    required: VALIDATION_ERROR_KEYS.REQUIRED,
                    valueAsNumber: true,
                  },
                )}
              />
              <ErrorMessage
                errors={errors}
                name={`${name}.${index}.duration` as const}
                render={ErrorHint}
              />
            </div>

            <div className="flex h-full items-end sm:items-center">
              <Button
                variant="destructive-outline"
                size="sm"
                type="button"
                onClick={() => remove(index)}
              >
                <MinusCircleIcon className="mr-1 h-4 w-4" />
                {t("common:actions.remove")}
              </Button>
            </div>
          </div>
        </fieldset>
      ))}

      {fields.length < 20 && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="mt-2 self-start"
          onClick={() => append({ count: 1, duration: 60 })}
        >
          <PlusCircle className="mr-1 h-4 w-4" />
          {t("common:actions.addItem", { item: t("common:models.session") })}
        </Button>
      )}
    </fieldset>
  )
}

const metricPillVariants = cva(
  "px-2 py-1 rounded text-sm flex items-center gap-1",
  {
    variants: {
      color: {
        gray: "bg-gray-100 text-gray-700",
        blue: "bg-blue-100 text-blue-700",
      },
    },
  },
)

const MetricPill = ({
  label,
  value,
  color,
}: {
  label: string
  value: string
  color: VariantProps<typeof metricPillVariants>["color"]
}) => (
  <div className={metricPillVariants({ color })}>
    <span className="font-medium">{label}:</span> {value}
  </div>
)
interface MetricPillGroupProps {
  cycles: number
  extraDays: number
  liveHours: number
  studentLedHours: number
  otherHours: number
  totalHours: number
}

const MetricPillGroup = ({
  cycles,
  liveHours,
  totalHours,
  extraDays,
  studentLedHours,
  otherHours,
}: MetricPillGroupProps) => {
  const { t } = useTranslation("custom_classes")
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-2">
        <MetricPill
          label={t("form.structure.live_hours")}
          value={liveHours.toString()}
          color="blue"
        />
        <MetricPill
          label={t("form.structure.student_led_hours")}
          value={studentLedHours.toString()}
          color="gray"
        />
        <MetricPill
          label={t("form.structure.other_hours")}
          value={otherHours.toString()}
          color="gray"
        />
        <MetricPill
          label={t("form.structure.total")}
          value={totalHours.toString()}
          color="gray"
        />
      </div>
      <div className="flex flex-row gap-2">
        <MetricPill
          label={t("form.structure.cycles")}
          value={cycles.toString()}
          color="blue"
        />
        <MetricPill
          label={t("form.structure.extra_days")}
          value={extraDays.toString()}
          color="gray"
        />
      </div>
    </div>
  )
}
function TeachingYearPlanner() {
  const { t } = useTranslation(["custom_classes", "common"])
  const { control, watch, setValue } = useFormContext<{
    custom_class: CustomClassFormValues
  }>()
  const [
    teaching_calendar,
    live_sessions,
    student_led_sessions,
    other_sessions,
    cycle_length,
  ] = watch([
    "custom_class.teaching_calendar",
    "custom_class.live_sessions_attributes",
    "custom_class.student_led_sessions_attributes",
    "custom_class.other_sessions_attributes",
    "custom_class.cycle_length",
  ])

  const { calendar } = useProps<{ calendar: TeachingCalendar }>()

  useEffect(() => {
    calendar &&
      setValue("custom_class.teaching_calendar", calendar, {
        shouldDirty: true,
        shouldValidate: true,
      })
  }, [calendar, setValue])

  const sum = (array: number[]) => array.reduce((acc, val) => acc + val, 0)

  const semesters = teaching_calendar.semesters || []
  const semesterMetrics = semesters.map((semester) => {
    const terms = semester.terms.map((term) => {
      const startDate = Temporal.PlainDate.from(term.start_date)
      const endDate = Temporal.PlainDate.from(term.end_date)
      const days = endDate.since(startDate).total({ unit: "days" })
      const cycles = Math.floor(days / cycle_length)
      const extraDays = days % cycle_length
      const caluclateTotalHours = (
        acc: number,
        session: { count: number; duration: number },
      ) => acc + (session.count || 0) * (session.duration / 60 || 0)
      const liveHours = Math.floor(
        live_sessions.reduce(caluclateTotalHours, 0) * cycles,
      )
      const studentLedHours = Math.floor(
        student_led_sessions.reduce(caluclateTotalHours, 0) * cycles,
      )
      const otherHours = Math.floor(
        other_sessions.reduce(caluclateTotalHours, 0) * cycles,
      )
      const totalHours = liveHours + studentLedHours + otherHours

      return {
        cycles,
        extraDays,
        liveHours,
        studentLedHours,
        otherHours,
        totalHours,
        startDate,
        endDate,
        label: term.label,
      }
    })

    return {
      terms,
      cycles: sum(terms.map((term) => term.cycles)),
      extraDays: sum(terms.map((term) => term.extraDays)),
      liveHours: sum(terms.map((term) => term.liveHours)),
      studentLedHours: sum(terms.map((term) => term.studentLedHours)),
      otherHours: sum(terms.map((term) => term.otherHours)),
      totalHours: sum(terms.map((term) => term.totalHours)),
      label: semester.label,
    }
  })

  const calendarMetrics = {
    semesters: semesterMetrics,
    cycles: sum(semesterMetrics.map((semester) => semester.cycles)),
    extraDays: sum(semesterMetrics.map((semester) => semester.extraDays)),
    liveHours: sum(semesterMetrics.map((semester) => semester.liveHours)),
    studentLedHours: sum(
      semesterMetrics.map((semester) => semester.studentLedHours),
    ),
    otherHours: sum(semesterMetrics.map((semester) => semester.otherHours)),
    totalHours: sum(semesterMetrics.map((semester) => semester.totalHours)),
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border border-gray-200 p-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row gap-2">
            <CalendarDays />
            <div className="text-lg font-semibold">
              {t("custom_classes:form.structure.school_year_planner")}
            </div>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="secondary"
                size="sm"
                disabled={semesters.length === 0}
              >
                {t("custom_classes:form.structure.edit_term_dates.button")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-fit">
              <DialogHeader>
                <DialogTitle>
                  {t(
                    "custom_classes:form.structure.edit_term_dates.dialog_title",
                  )}
                </DialogTitle>
                <DialogDescription>
                  {t(
                    "custom_classes:form.structure.edit_term_dates.dialog_description",
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className={cn("grid gap-2")}>
                {semesters.map((semester, semesterIndex) => (
                  <Fragment key={semesterIndex}>
                    <div className="text-md font-semibold">
                      {semester.label}
                    </div>
                    {semester.terms.map((term, termIndex) => (
                      <div
                        key={termIndex}
                        className="flex flex-row items-center gap-2"
                      >
                        <div className="min-w-16">{term.label}</div>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              id="date"
                              variant={"outline"}
                              className={cn(
                                "w-75 justify-start text-left font-normal",
                                !term && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon />
                              {Temporal.PlainDate.from(
                                term.start_date,
                              ).toLocaleString()}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto p-0"
                            align="center"
                            style={{ pointerEvents: "auto" }}
                          >
                            <Controller
                              name={`custom_class.teaching_calendar.semesters.${semesterIndex}.terms.${termIndex}.start_date`}
                              control={control}
                              render={({ field }) => (
                                <Calendar
                                  initialFocus
                                  mode="single"
                                  selected={new Date(field.value)}
                                  onSelect={(date) => {
                                    const isoDateString = (date: Date) =>
                                      Temporal.Instant.fromEpochMilliseconds(
                                        date.getTime(),
                                      )
                                        .toZonedDateTimeISO(
                                          Temporal.Now.timeZoneId(),
                                        )
                                        .toPlainDate()
                                        .toString()
                                    const value = isoDateString(date!)
                                    field.onChange(value)
                                  }}
                                  defaultMonth={new Date(term.start_date)}
                                  today={undefined}
                                />
                              )}
                            />
                          </PopoverContent>
                        </Popover>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              id="date"
                              variant={"outline"}
                              className={cn(
                                "w-75 justify-start text-left font-normal",
                                !term && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon />
                              {Temporal.PlainDate.from(
                                term.end_date,
                              ).toLocaleString()}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto p-0"
                            align="center"
                            style={{ pointerEvents: "auto" }}
                          >
                            <Controller
                              name={`custom_class.teaching_calendar.semesters.${semesterIndex}.terms.${termIndex}.end_date`}
                              control={control}
                              render={({ field }) => (
                                <Calendar
                                  initialFocus
                                  mode="single"
                                  selected={new Date(field.value)}
                                  onSelect={(date) => {
                                    const isoDateString = (date: Date) =>
                                      date
                                        .toTemporalInstant()
                                        .toZonedDateTimeISO(
                                          Temporal.Now.timeZoneId(),
                                        )
                                        .toPlainDate()
                                        .toString()
                                    const value = isoDateString(date!)
                                    field.onChange(value)
                                  }}
                                  defaultMonth={new Date(term.end_date)}
                                  today={undefined}
                                />
                              )}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    ))}
                  </Fragment>
                ))}
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button">{t("common:actions.close")}</Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <MetricPillGroup {...calendarMetrics} />
      </div>
      <Separator />
      {semesters.length === 0 && (
        <span className="text-sm">
          {t("custom_classes:form.structure.no_school_calendar_loaded")}
        </span>
      )}
      {calendarMetrics.semesters.map((semester, index) => (
        <Fragment key={index}>
          <div className="flex flex-col justify-between gap-2 p-2">
            <div className="text-md font-semibold">{semester.label}</div>
            <MetricPillGroup {...semester} />
          </div>
          {semester.terms.map((term, index) => (
            <Fragment key={index}>
              <div className="flex flex-col justify-between gap-2 p-2 pl-4">
                <div className="flex flex-row gap-2">
                  <div className="text-md font-semibold">{term.label}</div>
                  <div>
                    {term.startDate.toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}{" "}
                    -{" "}
                    {term.endDate.toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
                <MetricPillGroup {...term} />
              </div>
            </Fragment>
          ))}
          {index < calendarMetrics.semesters.length - 1 && <Separator />}
        </Fragment>
      ))}
    </div>
  )
}
